import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dateLabel, freshDraft, messagingRequest as api, readable, requestKey } from './api';
import DeliverySettings from './Settings';
import Inbox from './Inbox';
import { Field, Status } from './UI';
import './Messages.css';

const templateChoices = [
  {
    name: 'Appointment invitation',
    body:
      'Hi {{first_name}}, it’s {{rep_name}} at {{store_name}}. Are you still looking for a home? I can help you compare a few options. What day works for a visit?'
  },
  {
    name: 'Reconnect',
    body:
      'Hi {{first_name}}, {{rep_name}} at {{store_name}} here. Last time we spoke, you were exploring homes. Has anything changed with your plans?'
  },
  {
    name: 'New home options',
    body:
      'Hi {{first_name}}, it’s {{rep_name}} with {{store_name}}. I have some home options to show you. Are you looking for a single-section or a multi-section home?'
  }
];

function CampaignEditor({ meta, initial, onClose, onSaved, notify }) {
  const [draft, setDraft] = useState(initial),
    [busy, setBusy] = useState(false),
    [preview, setPreview] = useState(null),
    [dirty, setDirty] = useState(false);
  const [review, setReview] = useState(false),
    [confirmed, setConfirmed] = useState(false),
    [schedule, setSchedule] = useState('now'),
    [when, setWhen] = useState('');
  const launchKey = useRef(requestKey()),
    inFlight = useRef(false),
    mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  useEffect(() => {
    const warn = e => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const change = patch => {
    setDraft(d => ({ ...d, ...patch }));
    setDirty(true);
    setPreview(null);
    setConfirmed(false);
    launchKey.current = requestKey();
  };
  const filter = (name, value) => change({ filters: { ...draft.filters, [name]: value } });
  const work = async fn => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const save = async () => {
    const saved = await api('/campaigns' + (draft._id ? '/' + draft._id : ''), draft, draft._id ? 'PUT' : 'POST');
    setDraft(saved);
    setDirty(false);
    onSaved();
    return saved;
  };
  const previewAudience = () =>
    work(async () => {
      const saved = dirty || !draft._id ? await save() : draft;
      const result = await api('/campaigns/' + saved._id + '/preview', {});
      setPreview(result);
      setConfirmed(false);
    });
  const launch = () =>
    work(async () => {
      if (!confirmed || !preview || dirty) return;
      let scheduledAt;
      if (schedule === 'later') {
        const value = new Date(when);
        if (!when || !Number.isFinite(value.getTime())) throw new Error('Choose a date and time.');
        scheduledAt = value.toISOString();
      }
      const result = await api('/campaigns/' + draft._id + '/launch', {
        requestKey: launchKey.current,
        previewHash: preview.previewHash,
        scheduledAt
      });
      notify('Campaign queued for ' + dateLabel(result.scheduledAt) + '.');
      onSaved();
      onClose();
    });
  const close = () => {
    if (!busy && (!dirty || window.confirm('Leave this campaign without saving your latest changes?'))) onClose();
  };
  const eligible = preview?.counts?.eligible || 0;
  return (
    <section className="msg-editor">
      <div className="msg-editor-top">
        <button className="msg-link" onClick={close} disabled={busy}>
          ← Campaigns
        </button>
        <span>{dirty ? 'Unsaved changes' : draft._id ? 'Draft saved' : 'New campaign'}</span>
      </div>
      <div className="msg-section-heading">
        <div>
          <h2>{draft._id ? 'Edit campaign' : 'Create a campaign'}</h2>
          <p>Choose the audience. Make it personal. Start a conversation.</p>
        </div>
        <button
          className="msg-button secondary"
          disabled={busy}
          onClick={() =>
            work(async () => {
              await save();
              notify('Draft saved.');
            })
          }
        >
          Save draft
        </button>
      </div>
      <div className="msg-compose-grid">
        <div className="msg-panel">
          <h3>
            <span className="msg-step">1</span> Choose your audience
          </h3>
          <Field
            label="Campaign name"
            value={draft.name}
            maxLength={100}
            onChange={e => change({ name: e.target.value })}
            placeholder="Saturday appointments — Von Ormy"
          />
          <Field label="Channel">
            <select value={draft.channel} onChange={e => change({ channel: e.target.value })}>
              <option value="sms">Text message</option>
              <option value="email">Email</option>
            </select>
          </Field>
          <Field label="Store">
            <select value={draft.storeId} onChange={e => change({ storeId: e.target.value, filters: {} })}>
              <option value="">Choose a store</option>
              {meta.stores.map(s => (
                <option key={s._id} value={s._id}>
                  {s.name || s.location}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Salesperson">
            <select value={draft.filters.ownerId || ''} onChange={e => filter('ownerId', e.target.value)}>
              <option value="">All contacts I can access</option>
              {meta.owners.map(o => (
                <option value={o._id} key={o._id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="msg-form-pair">
            <Field label="Pipeline stage">
              <select value={draft.filters.status || ''} onChange={e => filter('status', e.target.value)}>
                <option value="">All stages</option>
                {meta.statuses.map(s => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Lead source">
              <select value={draft.filters.leadSource || ''} onChange={e => filter('leadSource', e.target.value)}>
                <option value="">All sources</option>
                {meta.sources.map(s => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Last contacted">
            <select value={draft.filters.noContactDays || ''} onChange={e => filter('noContactDays', e.target.value)}>
              <option value="">Any time</option>
              <option value="7">More than 7 days ago / never</option>
              <option value="30">More than 30 days ago / never</option>
              <option value="90">More than 90 days ago / never</option>
            </select>
          </Field>
          <Field
            label="Customer name contains"
            value={draft.filters.search || ''}
            onChange={e => filter('search', e.target.value)}
            placeholder="Optional"
          />
          <p className="msg-muted">
            Only contacts you can access with recorded permission for this channel are eligible. Opt-outs and duplicate
            addresses are excluded.
          </p>
        </div>
        <div className="msg-panel">
          <h3>
            <span className="msg-step">2</span> Write your message
          </h3>
          <Field label="Start from a template">
            <select
              defaultValue=""
              onChange={e => {
                if (e.target.value !== '') change({ body: templateChoices[Number(e.target.value)].body });
                e.target.value = '';
              }}
            >
              <option value="">Choose a starting point</option>
              {templateChoices.map((t, i) => (
                <option key={t.name} value={i}>
                  {t.name}
                </option>
              ))}
            </select>
          </Field>
          {draft.channel === 'email' && (
            <Field
              label="Subject"
              value={draft.subject}
              maxLength={160}
              onChange={e => change({ subject: e.target.value })}
            />
          )}
          <Field label="Message">
            <textarea
              rows={8}
              value={draft.body}
              maxLength={draft.channel === 'sms' ? 1200 : 12000}
              onChange={e => change({ body: e.target.value })}
              placeholder="Hi {{first_name}}, …"
            />
          </Field>
          <div className="msg-token-list">
            {meta.personalization.map(p => (
              <button key={p} onClick={() => change({ body: draft.body + '{{' + p + '}}' })}>
                {readable(p)}
              </button>
            ))}
          </div>
          <div className="msg-message-count">
            {draft.body.length.toLocaleString()} characters
            {draft.channel === 'sms' && ' · Personalization, links, and the opt-out line can add text segments.'}
          </div>
          <Field
            label="Home or appointment link"
            value={draft.link}
            onChange={e => change({ link: e.target.value })}
            placeholder="https://…"
            hint="Optional. Use an MHC or your store’s approved website link. Clicks are recorded."
          />
          <div className="msg-note">
            {draft.channel === 'sms'
              ? 'The store name and “Reply STOP to opt out” are added automatically. Each customer receives an individual text.'
              : 'Your store’s mailing address and an unsubscribe link are added automatically. Email replies go to the configured Reply-to inbox.'}
          </div>
        </div>
      </div>
      <div className="msg-panel msg-review-panel">
        <div className="msg-section-heading">
          <div>
            <h3>
              <span className="msg-step">3</span> Preview &amp; send
            </h3>
            <p>See the actual recipients and personalized messages before launching.</p>
          </div>
          <button className="msg-button secondary" disabled={busy} onClick={previewAudience}>
            {busy ? 'Working…' : 'Preview audience'}
          </button>
        </div>
        {preview && (
          <>
            <div className="msg-metrics">
              {[
                ['Matched', preview.counts.matched],
                ['Eligible', eligible],
                ['Missing permission', preview.counts.noPermission],
                ['Opted out', preview.counts.optedOut],
                ['Invalid address', preview.counts.noAddress],
                ['Duplicates', preview.counts.duplicate]
              ].map(([label, count]) => (
                <div key={label}>
                  <strong>{count}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>
            {!preview.ready && (
              <p className="msg-note">
                This store’s {draft.channel === 'sms' ? 'texting' : 'email'} delivery is paused or not connected. You
                can save and preview drafts while setup is completed.
              </p>
            )}
            {preview.samples.length > 0 && (
              <details open>
                <summary>Personalized previews · first {preview.samples.length}</summary>
                <div className="msg-preview-list">
                  {preview.samples.map(s => (
                    <article key={s.destination}>
                      <strong>{s.name}</strong>
                      <small>{s.destination}</small>
                      {s.subject && <b>{s.subject}</b>}
                      <p>{s.body}</p>
                    </article>
                  ))}
                </div>
              </details>
            )}
            {preview.excluded.length > 0 && (
              <details>
                <summary>Why contacts were excluded · first {preview.excluded.length}</summary>
                <ul>
                  {preview.excluded.map(s => (
                    <li key={s.id}>
                      <Link to={'/messages?contact=' + s.id}>{s.name}</Link> —{' '}
                      {
                        {
                          noAddress: 'No valid address',
                          noPermission: 'Permission not recorded',
                          optedOut: 'Opted out',
                          duplicate: 'Duplicate address'
                        }[s.reason]
                      }
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <div className="msg-editor-actions">
              <button
                className="msg-button secondary"
                disabled={busy || !preview.ready}
                onClick={() =>
                  work(async () => {
                    const r = await api('/campaigns/' + draft._id + '/test', { requestKey: requestKey() });
                    notify('Test queued to your profile address: ' + r.destination);
                  })
                }
              >
                Send test to myself
              </button>
              <button
                className="msg-button"
                disabled={busy || !preview.ready || !eligible}
                onClick={() => setReview(true)}
              >
                Review {eligible.toLocaleString()} recipients
              </button>
            </div>
          </>
        )}
      </div>
      {review && (
        <div className="msg-modal-backdrop">
          <section className="msg-confirm" role="dialog" aria-modal="true" aria-labelledby="msg-review-title">
            <h2 id="msg-review-title">Ready to send?</h2>
            <p>
              <strong>{draft.name}</strong>
              <br />
              {eligible.toLocaleString()} individual {draft.channel === 'sms' ? 'text messages' : 'emails'} will be
              queued.
            </p>
            <Field label="Delivery">
              <select value={schedule} onChange={e => setSchedule(e.target.value)}>
                <option value="now">Send as soon as available</option>
                <option value="later">Schedule for later</option>
              </select>
            </Field>
            {schedule === 'later' && (
              <Field
                label={'Schedule time · ' + timezone}
                type="datetime-local"
                value={when}
                onChange={e => setWhen(e.target.value)}
              />
            )}
            <p className="msg-muted">
              Texts send between 9 AM and 8 PM in the store’s time zone. Sending runs in batches, with a limit of 1,000
              messages per store and channel in 24 hours. You can cancel messages still waiting.
            </p>
            <label className="msg-checkbox">
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />I reviewed the
              audience and message.
            </label>
            <div className="msg-editor-actions">
              <button className="msg-button secondary" disabled={busy} onClick={() => setReview(false)}>
                Back
              </button>
              <button
                className="msg-button"
                disabled={busy || !confirmed || (schedule === 'later' && !when)}
                onClick={launch}
              >
                {busy ? 'Queuing…' : schedule === 'later' ? 'Schedule campaign' : 'Launch campaign'}
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

export default function Messages() {
  const location = useLocation(),
    contactId = new URLSearchParams(location.search).get('contact');
  const [meta, setMeta] = useState(null),
    [campaigns, setCampaigns] = useState([]),
    [tab, setTab] = useState(contactId ? 'inbox' : 'campaigns');
  const [editor, setEditor] = useState(null),
    [results, setResults] = useState(null),
    [notice, setNotice] = useState(null),
    [loading, setLoading] = useState(true);
  const notify = useCallback((message, type = 'success') => setNotice({ message, type }), []);
  const refresh = useCallback(async () => {
    try {
      const [m, c] = await Promise.all([api('/meta'), api('/campaigns')]);
      setMeta(m);
      setCampaigns(c.campaigns);
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    if (contactId) {
      setTab('inbox');
      setEditor(null);
      setResults(null);
    }
  }, [contactId]);
  const counts = campaigns.reduce((out, c) => {
    Object.entries(c.counts || {}).forEach(([key, n]) => {
      out[key] = (out[key] || 0) + n;
    });
    return out;
  }, {});
  const cancel = async c => {
    if (
      !window.confirm(
        'Cancel the remaining messages in “' + c.name + '”? Messages already being sent may still arrive.'
      )
    )
      return;
    try {
      await api('/campaigns/' + c._id + '/cancel', {});
      notify('Remaining messages cancelled.');
      refresh();
    } catch (e) {
      notify(e.message, 'error');
    }
  };
  return (
    <main className="mhc-messaging">
      <header className="msg-page-header">
        <div>
          <span className="msg-eyebrow">MHC · CUSTOMER CONVERSATIONS</span>
          <h1>Messages &amp; Campaigns</h1>
          <p>Keep the conversation moving.</p>
        </div>
        {meta && (
          <span className={'msg-connection ' + (meta.deliveryEnabled ? 'enabled' : '')}>
            {meta.deliveryEnabled ? 'Delivery enabled' : 'Delivery paused'}
          </span>
        )}
      </header>
      {notice && (
        <div className={'msg-notice ' + notice.type} role={notice.type === 'error' ? 'alert' : 'status'}>
          <span>{notice.message}</span>
          <button aria-label="Dismiss message" onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      )}
      {loading ? (
        <p className="msg-empty" role="status">
          Loading messaging…
        </p>
      ) : !meta ? (
        <div className="msg-empty">
          <p>Messaging could not be loaded.</p>
          <button className="msg-button" onClick={refresh}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {!editor && (
            <nav className="msg-tabs" aria-label="Messaging sections">
              {[
                ['campaigns', 'Campaigns'],
                ['inbox', 'Text inbox'],
                ...(meta.user.canConfigure ? [['settings', 'Delivery setup']] : [])
              ].map(([key, label]) => (
                <button
                  key={key}
                  aria-current={tab === key ? 'page' : undefined}
                  onClick={() => {
                    setTab(key);
                    setResults(null);
                  }}
                >
                  {label}
                </button>
              ))}
            </nav>
          )}
          {editor ? (
            <CampaignEditor
              key={editor._id || 'new'}
              initial={editor}
              meta={meta}
              onClose={() => setEditor(null)}
              onSaved={refresh}
              notify={notify}
            />
          ) : tab === 'settings' ? (
            <DeliverySettings meta={meta} notify={notify} onSaved={refresh} />
          ) : tab === 'inbox' ? (
            <Inbox meta={meta} initialContact={contactId} notify={notify} />
          ) : results ? (
            <section className="msg-panel">
              <button className="msg-link" onClick={() => setResults(null)}>
                ← Campaigns
              </button>
              <div className="msg-section-heading">
                <div>
                  <h2>{results.campaign.name}</h2>
                  <p>
                    {results.replies} text replies attributed within 30 days · Showing up to {results.limitedTo}{' '}
                    recipients
                  </p>
                </div>
                <Status value={results.campaign.status} />
              </div>
              <div className="msg-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Delivery</th>
                      <th>Current CRM stage</th>
                      <th>Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.rows.map(row => (
                      <tr key={row._id}>
                        <td>
                          <Link to={'/messages?contact=' + row.contact}>{row.customer?.buyer || 'Customer'}</Link>
                          <small>{row.destination}</small>
                        </td>
                        <td>
                          <Status value={row.status} />
                          {row.reason && <small>{row.reason}</small>}
                        </td>
                        <td>{row.customer?.status || '—'}</td>
                        <td>{row.firstClickAt ? dateLabel(row.firstClickAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="msg-muted">
                Clicks can include link scanners. Current CRM stages show where these customers are now; they do not
                prove this campaign caused a sale.
              </p>
            </section>
          ) : (
            <>
              <div className="msg-metrics msg-overview">
                <div>
                  <strong>{campaigns.length}</strong>
                  <span>Recent campaigns</span>
                </div>
                <div>
                  <strong>{counts.queued || 0}</strong>
                  <span>Waiting to send</span>
                </div>
                <div>
                  <strong>{counts.delivered || 0}</strong>
                  <span>Delivered</span>
                </div>
                <div>
                  <strong>
                    {(counts.failed || 0) +
                      (counts.unknown || 0) +
                      (counts.undelivered || 0) +
                      (counts.bounced || 0) +
                      (counts.complained || 0)}
                  </strong>
                  <span>Need attention</span>
                </div>
              </div>
              <div className="msg-section-heading">
                <div>
                  <h2>Your campaigns</h2>
                  <p>One message. The right audience. Individual conversations.</p>
                </div>
                <div className="msg-inline-actions">
                  <button className="msg-button secondary" onClick={refresh}>
                    Refresh
                  </button>
                  <button className="msg-button" onClick={() => setEditor(freshDraft(meta.stores[0]?._id))}>
                    + New campaign
                  </button>
                </div>
              </div>
              {!campaigns.length ? (
                <div className="msg-empty">
                  <span className="msg-empty-icon" aria-hidden="true">
                    ✉
                  </span>
                  <h3>Start your first conversation</h3>
                  <p>Create a draft, select a store, and preview your audience.</p>
                  <button className="msg-button" onClick={() => setEditor(freshDraft(meta.stores[0]?._id))}>
                    Create campaign
                  </button>
                </div>
              ) : (
                <div className="msg-campaign-list">
                  {campaigns.map(c => (
                    <article key={c._id} className="msg-campaign-card">
                      <div className="msg-campaign-icon" aria-hidden="true">
                        {c.channel === 'sms' ? 'SMS' : '@'}
                      </div>
                      <div className="msg-campaign-main">
                        <h3>{c.name}</h3>
                        <p>
                          {meta.stores.find(s => s._id === c.storeId)?.name || 'Store'} ·{' '}
                          {c.channel === 'sms' ? 'Text message' : 'Email'} · {dateLabel(c.scheduledAt || c.createdAt)}
                        </p>
                        <div className="msg-inline-stats">
                          {Object.entries(c.counts || {}).map(([state, n]) => (
                            <span key={state}>
                              {n} {readable(state).toLowerCase()}
                            </span>
                          ))}
                          {c.clicked > 0 && <span>{c.clicked} recipients clicked</span>}
                        </div>
                      </div>
                      <div className="msg-campaign-actions">
                        <Status value={c.status} />
                        <div className="msg-inline-actions">
                          {c.status === 'draft' ? (
                            <button className="msg-button secondary" onClick={() => setEditor(c)}>
                              Edit draft
                            </button>
                          ) : (
                            <button
                              className="msg-button secondary"
                              onClick={async () => {
                                try {
                                  setResults(await api('/campaigns/' + c._id + '/results'));
                                } catch (e) {
                                  notify(e.message, 'error');
                                }
                              }}
                            >
                              Results
                            </button>
                          )}
                          {['queued', 'sending', 'launching'].includes(c.status) && (
                            <button className="msg-link danger" onClick={() => cancel(c)}>
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </main>
  );
}
